export const minimalAge = 18;
export const maximalAge = 70;

export const minimalHeight = 120;
export const maximalHeight = 250;

import { locales } from '@bells/i18n/runtime';
import { tags } from '@bells/iso/data/countries';
import {
  AttendantSearchRequestSchema,
  AttendantSearchSortBy,
  BreastScope,
  ClothesConversion,
  Gos,
  HairColor,
  HasSilicon,
  MobilityStatus,
  NoCustomer,
  NoneEnum,
  Piercing,
  SexualOrientation,
  SmokerStatus,
  Tattoo,
  ThreesomeStatus,
  Toy,
  VisitStatus,
} from '@bells/schema/models/attendant';
import { isEmpty, isInteger, toNumber } from 'lodash';
import { z } from 'zod';

export const AttendantLimit = 23;
export const extractPage = (slug: string[] | undefined) => {
  if (typeof slug !== 'object') {
    return 1;
  } else {
    const findPageIndex = slug.findIndex((df) => df === 'page');
    if (
      findPageIndex > -1 &&
      slug[findPageIndex + 1] &&
      (typeof slug[findPageIndex + 1] == 'string' ||
        typeof slug[findPageIndex + 1] == 'number')
    ) {
      const number = toNumber(slug[findPageIndex + 1]);

      if (!isNaN(number) && isInteger(number)) {
        return number;
      } else {
        return 1;
      }
    } else {
      return 1;
    }
  }
};
export type FilterParams = z.infer<typeof AttendantSearchRequestSchema>;

export type FilterSelection = Partial<
  Record<keyof FilterParams, string | string[]>
>;
export const defaultFilter: FilterSelection = {
  sortBy: AttendantSearchSortBy.DISTANCE_ASC.toString(),
  visits: '',
  services: [],
  origins: [],
  hasSilicon: [],
  languages: [],
  mobility: [],
  toys: [],
  piercings: '',
  hairColors: [],
  tattoos: '',
  customers: [],
  sexualOrientation: '',
  breastScope: [],
  gos: [],
  smoker: '',
  clothesSize: [],
  ageFrom: minimalAge.toString(),
  ageTo: maximalAge.toString(),
  heightFrom: minimalHeight.toString(),
  heightTo: maximalHeight.toString(),
  page: '1',
  limit: AttendantLimit.toString(),
};

const stringToNumberTransform = z
  .string()
  .optional()
  .transform((val) => {
    if (isEmpty(val)) {
      return undefined;
    }
    const num = Number(val);
    return isNaN(num) ? undefined : num;
  });

const createStringToEnumArrayTransform = <T extends object>(enumType: T) => {
  const validValuesArray = Object.values(enumType);
  return z
    .string()
    .optional()
    .transform((val) => {
      if (!val || isEmpty(val)) {
        return undefined;
      }

      const result = val
        .split(',')
        .map((item) => item.trim())
        .filter((item) => validValuesArray.includes(item))
        .map((item) => {
          const enumKey = Object.keys(enumType).find(
            (key) => enumType[key as keyof T] === item
          );
          return enumType[enumKey as keyof T];
        });

      return result.length > 0 ? result : undefined;
    });
};

const createStringToEnumTransform = <T extends object>(enumType: T) => {
  const validValuesArray = Object.values(enumType);
  return z
    .string()
    .optional()
    .transform((val) => {
      if (!val || isEmpty(val)) {
        return undefined;
      }

      if (!validValuesArray.includes(val)) {
        return undefined;
      }

      const enumKey = Object.keys(enumType).find(
        (key) => enumType[key as keyof T] === val
      );
      return enumType[enumKey as keyof T];
    });
};

const createStringToArrayTransform = () => {
  return z
    .string()
    .optional()
    .transform((val) => {
      if (!val || isEmpty(val)) {
        return undefined;
      }
      return val.split(',').map((item) => item.trim());
    });
};

export const AttendantSearchParamsSchema = z
  .object({
    start: z.string().optional(),
    lng: stringToNumberTransform,
    lat: stringToNumberTransform,
    duration: stringToNumberTransform,
    page: stringToNumberTransform,
    limit: stringToNumberTransform,
    ageFrom: stringToNumberTransform,
    ageTo: stringToNumberTransform,
    heightFrom: stringToNumberTransform,
    heightTo: stringToNumberTransform,
    origins: createStringToArrayTransform(),
    languages: createStringToArrayTransform(),
    language: z.enum(locales).optional(),
    country: z.enum(tags).optional(),
    clothesSize: createStringToEnumArrayTransform(ClothesConversion),
    breastScope: createStringToEnumArrayTransform(BreastScope),
    hasSilicon: createStringToEnumArrayTransform(HasSilicon),
    hairColors: createStringToEnumArrayTransform(HairColor),
    customers: createStringToEnumArrayTransform(NoCustomer),
    services: createStringToEnumArrayTransform(ThreesomeStatus),
    mobility: createStringToEnumArrayTransform(MobilityStatus),
    toys: createStringToEnumArrayTransform(Toy),
    gos: createStringToEnumArrayTransform(Gos),
    sexualOrientation: createStringToEnumTransform(SexualOrientation),
    smoker: createStringToEnumTransform(SmokerStatus),
    sortBy: createStringToEnumTransform(AttendantSearchSortBy),
    visits: createStringToEnumTransform(VisitStatus),
    tattoos: createStringToEnumTransform(Tattoo),
    piercings: createStringToEnumTransform(Piercing)
      .or(createStringToEnumTransform(NoneEnum))
      .optional(),
    name: z.string().optional(),
    travelCompanion: z.boolean().optional(),
  })
  .partial();

export const SearchParamsSchema = z
  .record(z.string().optional())
  .optional()
  .transform((obj) => {
    if (obj == undefined) {
      return undefined;
    }
    return Object.fromEntries(
      Object.entries(obj).map(([key, value]) => {
        if (key in AttendantSearchParamsSchema.shape) {
          const schema =
            AttendantSearchParamsSchema.shape[
              key as keyof typeof AttendantSearchParamsSchema.shape
            ];

          const newValue =
            value === 'true' ? true : value === 'false' ? false : value;

          const result = schema.safeParse(newValue);
          return [key, result.success ? result.data : undefined];
        }
        return [key, value];
      })
    );
  });

export type AttendantSearchParams = z.infer<typeof AttendantSearchParamsSchema>;

export const checkHasFilters = (filters: any) => {
  let totalFilters = 0;
  for (const [key, value] of Object.entries(filters)) {
    if (value == undefined) {
      continue;
    }
    if (
      key == 'sortBy' ||
      key == 'name' ||
      key == 'page' ||
      key == 'start' ||
      key == 'duration' ||
      key == 'limit' ||
      key == 'lng' ||
      key == 'lat' ||
      key == 'language' ||
      key == 'country'
    ) {
      continue;
    }

    if (
      (key === 'ageFrom' && value === minimalAge) ||
      (key === 'ageTo' && value === maximalAge) ||
      (key === 'heightFrom' && value === minimalHeight) ||
      (key === 'heightTo' && value === maximalHeight)
    ) {
      continue;
    }

    totalFilters += 1;
  }
  return totalFilters;
};
